import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { NavLink } from 'react-router-dom'
import { toast } from 'react-toastify'
import ActionBar from '../../components/ActionBar'
import Search from '../../components/Search'
import Button from '../../components/shared/Button'
import Loader from '../../components/shared/Loader'
import Pagination from '../../components/shared/Pagination'
import Table from '../../components/Table'
import useFetch from '../../hooks/useFetch'
import CaptionLayout from '../../layouts/CaptionLayout'
import Section from '../../layouts/Section'
import SectionBody from '../../layouts/SectionBody'
import {
  getSpiderProductCategories,
  getSpiderProducts,
  getSpiderProductSources,
  importSpiderProducts,
} from '../../utils/Apis/products'
import { cn } from '../../utils/helperFunctions'
import { withErrorHandling } from '../../utils/withErrorHandling'
import { createColumns } from './COLUMNS'

/**
 * 爬虫商品预览与快速导入页面。
 * @returns {JSX.Element} 页面组件
 */
const SpiderProducts = ({ className, ...props }) => {
  const [currentPage, setCurrentPage] = useState(1)
  const [search, setSearch] = useState('')
  const [source, setSource] = useState('')
  const [categoryId, setCategoryId] = useState('')
  const [selectedCodes, setSelectedCodes] = useState([])
  const [importing, setImporting] = useState(false)
  const [downloadImagesLocally, setDownloadImagesLocally] = useState(true)

  const { data: sourceData } = useFetch({
    fn: () => getSpiderProductSources(),
  })
  const {
    data: categoryData,
    loading: categoryLoading,
    refetch: refetchCategories,
  } = useFetch({
    fn: (selectedSource) =>
      selectedSource || source
        ? getSpiderProductCategories(selectedSource || source)
        : Promise.resolve({ data: [] }),
  })
  const {
    data,
    error,
    loading,
    refetch,
  } = useFetch({
    fn: (query = {}) =>
      source
        ? getSpiderProducts({
            source,
            categoryId,
            page: currentPage,
            limit: 20,
            keyword: search,
            ...query,
          })
        : Promise.resolve({
            totalCount: 0,
            results: 0,
            paginationResult: {
              currentPage: 1,
              limit: 20,
              numberOfPages: 1,
            },
            data: [],
          }),
  })

  /**
   * 获取当前筛选条件下的爬虫商品。
   * @param {Record<string, any>} extraQuery 额外查询参数
   */
  const fetchSpiderProducts = useCallback(
    (extraQuery = {}) => {
      if (!source) {
        return
      }

      refetch({
        source,
        categoryId,
        page: currentPage,
        limit: 20,
        keyword: search,
        ...extraQuery,
      })
    },
    [categoryId, currentPage, refetch, search, source],
  )

  /**
   * 处理关键词搜索。
   * @param {string} keyword 搜索关键词
   */
  const handleSearch = withErrorHandling(async (keyword) => {
    setCurrentPage(1)
    fetchSpiderProducts({
      keyword,
      page: 1,
    })
  })

  /**
   * 切换单条商品选中状态。
   * @param {string} productCode 商品编码
   */
  const handleToggleCode = useCallback((productCode) => {
    setSelectedCodes((currentSelected) => {
      if (currentSelected.includes(productCode)) {
        return currentSelected.filter((item) => item !== productCode)
      }

      return [...currentSelected, productCode]
    })
  }, [])

  const pageProducts = useMemo(() => data?.data || [], [data?.data])
  const selectableCodes = useMemo(
    () =>
      pageProducts
        .filter((item) => !item.imported)
        .map((item) => item.productCode),
    [pageProducts],
  )
  const isAllSelected =
    selectableCodes.length > 0 &&
    selectableCodes.every((item) => selectedCodes.includes(item))

  /**
   * 切换当前页全部可导入商品的选中状态。
   */
  const handleToggleAll = useCallback(() => {
    if (isAllSelected) {
      setSelectedCodes((currentSelected) =>
        currentSelected.filter((item) => !selectableCodes.includes(item)),
      )
      return
    }

    setSelectedCodes((currentSelected) => [
      ...new Set([...currentSelected, ...selectableCodes]),
    ])
  }, [isAllSelected, selectableCodes])

  /**
   * 执行爬虫商品批量导入。
   * @param {string[]} productCodes 待导入商品编码
   */
  const importProductsHandler = useCallback(
    async (productCodes) => {
      if (!Array.isArray(productCodes) || productCodes.length <= 0) {
        return toast.warning('请先选择要导入的商品')
      }

      setImporting(true)
      try {
        const response = await importSpiderProducts({
          source,
          productCodes,
          imageImportMode: downloadImagesLocally ? 'local' : 'remote',
        })
        toast.success(
          `导入完成：成功 ${response.importedCount} 条，跳过 ${response.skippedCount} 条，缺失 ${response.missingCount} 条，图片下载失败 ${response.imageDownloadFailedCount || 0} 条`,
        )
        setSelectedCodes((currentSelected) =>
          currentSelected.filter((item) => !productCodes.includes(item)),
        )
        fetchSpiderProducts()
      } finally {
        setImporting(false)
      }
    },
    [downloadImagesLocally, fetchSpiderProducts, source],
  )
  const handleImportProducts = useMemo(
    () => withErrorHandling(importProductsHandler),
    [importProductsHandler],
  )

  /**
   * 创建表格列定义。
   */
  const columns = useMemo(
    () =>
      createColumns({
        importing,
        isAllSelected,
        selectableCodes,
        selectedCodes,
        onToggleAll: handleToggleAll,
        onToggleCode: handleToggleCode,
        onImportOne: (productCode) => handleImportProducts([productCode]),
      }),
    [
      importing,
      isAllSelected,
      selectableCodes,
      selectedCodes,
      handleImportProducts,
      handleToggleAll,
      handleToggleCode,
    ],
  )

  useEffect(() => {
    document.title = 'Spider Products - Admin'
  }, [])

  useEffect(() => {
    const firstSource = sourceData?.data?.[0]?.code
    if (!firstSource) {
      return
    }

    const hasCurrentSource = sourceData.data.some((item) => item.code === source)
    if (!hasCurrentSource) {
      setSource(firstSource)
    }
  }, [sourceData, source])

  useEffect(() => {
    refetchCategories(source)
    setCategoryId('')
    setSelectedCodes([])
    setCurrentPage(1)
  }, [refetchCategories, source])

  useEffect(() => {
    if (!source) {
      return
    }

    fetchSpiderProducts()
  }, [fetchSpiderProducts, source])

  useEffect(() => {
    if (!source) {
      return
    }

    if (search === '') {
      fetchSpiderProducts({
        keyword: '',
        page: currentPage,
      })
    }
  }, [currentPage, fetchSpiderProducts, search, source])

  return (
    <div className={cn('', className)} {...props}>
      <Section className={'lg:pb-2'}>
        <div className="flex w-full flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex flex-wrap items-center gap-3">
            <p className="text-sm text-gray-500">
              Selected {selectedCodes.length} item(s)
            </p>
            <label className="flex items-center gap-2 text-sm text-gray-600">
              <input
                type="checkbox"
                checked={downloadImagesLocally}
                onChange={(event) =>
                  setDownloadImagesLocally(event.target.checked)
                }
                disabled={importing}
                className="size-4"
              />
              下载图片到本地
            </label>
            <Button
              onClick={() => handleImportProducts(selectedCodes)}
              disabled={selectedCodes.length <= 0 || importing}
              className="bg-green-600 hover:bg-green-700"
            >
              Import Selected
            </Button>
            <NavLink to={'/products'}>
              <Button>All Products</Button>
            </NavLink>
          </div>
          <Search
            handleSearch={handleSearch}
            search={search}
            setSearch={setSearch}
            className={'static h-10 w-full xl:static xl:right-auto xl:w-auto'}
            placeholder={'Search spider product'}
          />
        </div>
        <ActionBar className={'top-0'}>
          <div className="flex w-full flex-col gap-3 lg:flex-row lg:items-center">
            <select
              value={source}
              onChange={(event) => setSource(event.target.value)}
              className="rounded-lg border border-primary/20 px-3 py-2 outline-none focus:ring-2"
              disabled={(sourceData?.data || []).length <= 0}
            >
              {(sourceData?.data || []).length <= 0 && (
                <option value="">No SQLite Source</option>
              )}
              {(sourceData?.data || []).map((item) => (
                <option key={item.code} value={item.code}>
                  {item.name}
                </option>
              ))}
            </select>
            <select
              value={categoryId}
              onChange={(event) => {
                setCategoryId(event.target.value)
                setCurrentPage(1)
                setSelectedCodes([])
              }}
              className="rounded-lg border border-primary/20 px-3 py-2 outline-none focus:ring-2"
              disabled={categoryLoading}
            >
              <option value="">All Categories</option>
              {(categoryData?.data || []).map((item) => (
                <option key={item.categoryId} value={item.categoryId}>
                  {item.label}
                </option>
              ))}
            </select>
          </div>
        </ActionBar>
      </Section>

      {loading ? (
        <Loader />
      ) : (sourceData?.data || []).length <= 0 ? (
        <SectionBody>
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-700">
            当前未发现可用的 SQLite 爬虫数据源。请先使用类似
            <code className="mx-1 rounded bg-white px-1 py-0.5">
              python .\tools\lcsc_catalog_spider.py --db-path .\output\lcsc\lcsc_data.db
            </code>
            的方式生成数据库后，再进入本页面预览。
          </div>
        </SectionBody>
      ) : error ? (
        <p>Error</p>
      ) : (
        <SectionBody>
          <CaptionLayout
            results={data?.totalCount}
            caption={'Spider products found'}
          >
            <Pagination
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              pages={data?.paginationResult?.numberOfPages}
              showGoToPage={true}
            />
          </CaptionLayout>
          <div
            className={cn(
              'custom-scroll relative max-h-[45vh] w-full overflow-auto md:max-h-[60vh]',
              className,
            )}
            {...props}
          >
            <Table data={pageProducts} columns={columns} />
            <div className="pointer-events-none sticky bottom-0 left-0 h-16 w-full bg-gradient-to-t from-background to-transparent" />
          </div>
        </SectionBody>
      )}
    </div>
  )
}

export default SpiderProducts
